/**
 * exercicesPlateau.js — Les 12 exercices plateau officiels
 *
 * Source : notes manuscrites terrain moniteur ECSR (cahier WA0000-WA0002).
 *
 * Structure : pour chaque exercice, un titre, un sous-titre (cadre / variantes),
 * une liste de critères-clés (ce que le moniteur observe) et une notation
 * optionnelle de l'allure/condition.
 *
 * Codes REMC associés : C1 (maniement maîtrise) — C1-a à C1-h selon livret REMC.
 *
 * Abréviations conservées telles que dans les notes du moniteur :
 *   - AR / AV : Frein Arrière / Avant
 *   - QA2 : repère moniteur (à préciser)
 *   - GLD : repère moniteur (à préciser)
 *   - DT  : Demi-Tour
 *   - PP teau : Pédale Plateau / repère plateau
 */

export const EXERCICES_PLATEAU = [
  {
    id: 1,
    code: 'plateau-1',
    titre: 'Démarrage – Arrêt',
    sousTitre: 'Mise en mouvement ↔ arrêt précis pied gauche au sol',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-a', 'C1-b'],
    criteres: [
      { label: 'Position', detail: 'Mains, corps, pieds, jambes' },
      { label: 'Regard', detail: 'Loin vers le repère, pas sur la roue' },
      { label: 'Embrayage', detail: 'Patinage avec et sans accélérateur' },
      { label: 'Arrêt', detail: 'Frein arrière ou frein avant selon consigne' },
      { label: 'Pied gauche au sol', detail: 'Pied droit reste sur frein arrière' },
    ],
    allure: 'Pas à pas — vitesse marche',
  },
  {
    id: 2,
    code: 'plateau-2',
    titre: 'Allure lente en ligne droite',
    sousTitre: 'Stabilité au pas / vitesse marche sans poser le pied',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-c', 'C1-d'],
    criteres: [
      { label: 'Position', detail: 'Buste droit, genoux serrés, regard haut' },
      { label: 'Regard', detail: 'Loin devant, jamais au sol' },
      { label: 'Embrayage', detail: 'Patinage avec / sans frein AR et accélérateur' },
    ],
    allure: 'Allure marche stabilisée (≈ 5-10 km/h)',
  },
  {
    id: 3,
    code: 'plateau-3',
    titre: 'Demi-tour au ralenti moteur',
    sousTitre: 'À gauche · à droite · en 8',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-c', 'C1-d'],
    variantes: ['À gauche', 'À droite', 'En 8'],
    criteres: [
      { label: 'Position', detail: 'Buste à l\'opposé du virage, transfert latéral' },
      { label: 'Regard', detail: 'Sur la sortie de virage, par-dessus l\'épaule' },
    ],
    allure: 'Ralenti moteur — embrayage tenu en patinage',
  },
  {
    id: 4,
    code: 'plateau-4',
    titre: 'Demi-tour à l\'embrayage',
    sousTitre: 'Maîtrise du patinage embrayage en virage serré',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-c', 'C1-d'],
    criteres: [
      { label: 'Position', detail: 'GD (Gauche / Droite selon sens)' },
      { label: 'Regard', detail: 'Loin sur la sortie' },
      { label: 'Embrayage', detail: 'Dosage fin du patinage pour réguler l\'allure' },
    ],
    allure: 'Patinage embrayage continu',
  },
  {
    id: 5,
    code: 'plateau-5',
    titre: 'Station à allure lente',
    sousTitre: 'Trajectoire imposée à très basse vitesse',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-c', 'C1-d'],
    criteres: [
      { label: 'Position', detail: 'Centrée sur la moto, mobile sur les appuis' },
      { label: 'Regard', detail: 'Loin sur la trajectoire' },
      { label: 'Trajectoire', detail: 'Suivre la ligne imposée — pas d\'écart' },
      { label: 'Embrayage', detail: 'Patinage régulier' },
      { label: 'Frein', detail: 'Frein arrière en appui pour stabiliser' },
    ],
    allure: 'Pas à pas — vitesse marche',
  },
  {
    id: 6,
    code: 'plateau-6',
    titre: 'Démarrage dynamique',
    sousTitre: '+ variante « 6 bis · DT Arrêté GLD »',
    categorie: 'dynamique',
    remcCodes: ['C1-a', 'C1-b'],
    sections: [
      {
        nom: 'Démarrage dynamique',
        criteres: [
          { label: 'Position', detail: 'Coudes ouverts, regard projeté loin' },
          { label: 'Regard', detail: 'Vers le 1er repère / la sortie' },
          { label: 'QA2', detail: 'Repère moniteur — quart arrière n°2' },
          { label: 'Embrayage', detail: 'Lâché progressif sans à-coup' },
        ],
      },
      {
        nom: '6 bis — Demi-tour arrêté (GLD)',
        criteres: [
          { label: 'Arrêt précis frein AR', detail: 'Arrêt net avec frein arrière' },
          { label: 'Position corps', detail: 'Transfert sur appui extérieur' },
          { label: 'Regard', detail: 'Par-dessus l\'épaule vers la sortie' },
          { label: 'Pied d\'appui', detail: 'Bon pied au sol selon le sens' },
          { label: 'Gaz + plateau', detail: 'Dosage gaz / patinage pour relancer' },
        ],
      },
    ],
    allure: '1ère / 2ème, démarrage franc',
  },
  {
    id: 7,
    code: 'plateau-7',
    titre: 'Passage des vitesses 1 · 2 · 3 + Frein',
    sousTitre: 'Accélération progressive et freinages d\'arrêt',
    categorie: 'dynamique',
    remcCodes: ['C1-e', 'C1-f', 'C1-g'],
    sections: [
      {
        nom: 'Passage de vitesses',
        criteres: [
          { label: 'Position', detail: 'Stable, buste relâché' },
          { label: 'Regard', detail: 'Loin devant' },
          { label: 'Allure', detail: 'Montée progressive et régulière' },
          { label: 'Sélecteur', detail: 'Coup de pied net, embrayage coupé' },
          { label: 'Synchro', detail: 'Embrayage + sélecteur + gaz synchronisés' },
          { label: 'Régime', detail: 'Passage au bon régime moteur' },
        ],
      },
      {
        nom: 'Freinage d\'arrêt en B',
        criteres: [
          { label: 'Arrêt en B', detail: 'Arrêt sur le repère imposé' },
          { label: 'Frein AR', detail: 'Variante frein arrière seul' },
          { label: 'Frein AV', detail: 'Variante frein avant seul' },
          { label: 'Frein AR + AV', detail: 'Freinage combiné — répartition 70 % AV / 30 % AR' },
        ],
      },
    ],
    allure: '1ère → 2ème → 3ème',
  },
  {
    id: 8,
    code: 'plateau-8',
    titre: 'Rétrogradage',
    sousTitre: 'Rétrograder 3 → 2 → 1 avec tenue du frein',
    categorie: 'dynamique',
    remcCodes: ['C1-e', 'C1-f'],
    criteres: [
      { label: 'Séquence', detail: '3 → 2 → 1, un rapport à la fois' },
      { label: 'Tenue du frein', detail: 'Frein maintenu pendant la rétrogradation' },
      { label: 'Embrayage', detail: 'Débrayage / réembrayage synchronisé' },
      { label: 'Régime', detail: 'Pas de sur-régime — pas de calage' },
    ],
    allure: 'Décélération depuis 3ème jusqu\'à l\'arrêt',
  },
  {
    id: 9,
    code: 'plateau-9',
    titre: 'Freinage d\'urgence',
    sousTitre: '35 / 45 / 55 km/h en 3ème — Frein AR + AV',
    categorie: 'urgence',
    remcCodes: ['C1-g', 'C1-h'],
    criteres: [
      { label: 'Position bras', detail: 'Bras tendus mais relâchés, coudes souples' },
      { label: 'Regard', detail: 'Loin devant, pas sur la roue' },
      { label: 'Frein AR + AV', detail: 'Combiné — pression progressive, sans blocage' },
      { label: 'Allure en 3ème', detail: '35 / 45 / 55 km/h selon palier' },
    ],
    paliers: ['35 km/h', '45 km/h', '55 km/h'],
    allure: '3ème stabilisée puis freinage d\'urgence',
    danger: true,
  },
  {
    id: 10,
    code: 'plateau-10',
    titre: 'Contre-braquage / Slalom',
    sousTitre: 'Allure en 3ème stabilisée — inclinaison maîtrisée',
    categorie: 'dynamique',
    remcCodes: ['C2-c'],
    criteres: [
      { label: 'Allure', detail: '3ème stabilisée (≈ 30-40 km/h selon parcours)' },
      { label: 'Inclinaison', detail: 'Bascule du buste / appui sur le repose-pied extérieur' },
      { label: 'Regard', detail: 'Sortie de chaque cône, jamais sur le cône en cours' },
      { label: 'Contre-braquage', detail: 'Pousser le guidon côté virage à >30 km/h' },
    ],
    allure: '3ème stabilisée',
  },
  {
    id: 11,
    code: 'plateau-11',
    titre: 'Évitement',
    sousTitre: 'Évitement d\'obstacle sans frein avant repéré',
    categorie: 'urgence',
    remcCodes: ['C2-c', 'C3-c'],
    criteres: [
      { label: 'Allure en 3ème', detail: 'Sans frein avant repéré (interdit dès la zone d\'évitement)' },
      { label: 'Contre-braquage', detail: 'Pulsion guidon franche côté évitement' },
      { label: 'Regard', detail: 'Sortie de l\'évitement, pas sur l\'obstacle' },
    ],
    allure: '3ème — vitesse imposée par l\'épreuve',
    danger: true,
  },
  {
    id: 12,
    code: 'plateau-12',
    titre: 'Demi-tour avec passager',
    sousTitre: 'DT Passager — équilibre + dosage avec masse arrière',
    categorie: 'maitrise-statique',
    remcCodes: ['C1-c', 'C1-d'],
    criteres: [
      { label: 'Briefing passager', detail: 'Pieds sur cale-pied, regard moniteur, ne pas s\'agripper aux bras' },
      { label: 'Position', detail: 'Compenser la masse arrière par un appui ferme' },
      { label: 'Regard', detail: 'Par-dessus l\'épaule sur la sortie' },
      { label: 'Embrayage', detail: 'Patinage plus consommateur — anticiper le besoin de gaz' },
    ],
    allure: 'Ralenti moteur — embrayage tenu',
  },
];

/**
 * Catégories pédagogiques pour regrouper les exercices.
 */
export const CATEGORIES_PLATEAU = {
  'maitrise-statique': {
    label: 'Maîtrise à basse vitesse',
    accent: '#0891b2',
    description: 'Exercices d\'équilibre, dosage, demi-tours et trajectoires lentes.',
  },
  dynamique: {
    label: 'Conduite dynamique',
    accent: '#7c3aed',
    description: 'Démarrages, passages de vitesses, contre-braquage et trajectoires fluides.',
  },
  urgence: {
    label: 'Réactions d\'urgence',
    accent: '#dc2626',
    description: 'Freinage d\'urgence, évitement — gestes critiques pour la sécurité.',
  },
};

/**
 * Glossaire des abréviations utilisées dans les notes manuscrites.
 */
export const GLOSSAIRE_PLATEAU = [
  { abrev: 'AR / AV', sens: 'Frein arrière / avant' },
  { abrev: 'DT', sens: 'Demi-tour' },
  { abrev: 'GLD', sens: 'Repère moniteur — variante de demi-tour arrêté (Gauche / Latéral / Droit selon le tracé)' },
  { abrev: 'QA2', sens: 'Repère moniteur — quart arrière n°2 (zone du parcours plateau)' },
  { abrev: 'PP teau / Plateau', sens: 'Repère plateau / dosage gaz sur le plateau d\'embrayage' },
  { abrev: 'Synchro', sens: 'Synchronisation embrayage + sélecteur + gaz lors du passage de vitesse' },
];

export default EXERCICES_PLATEAU;
